import ApolloClient, { PureQueryOptions } from 'apollo-client'
import { useCallback } from 'react'

export type RefreshGridDataFn = (
  refreshAfterNonGraphQLOperation?: boolean
) => void

interface Props {
  readonly apolloClient: {
    readonly query: ApolloClient<any>['query']
  }
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => void
  readonly children: (props: {
    readonly refreshGridData: RefreshGridDataFn
  }) => any
}

/**
 * GridDataRefetcher combines multiple "refetch" operations into one, which simplifies
 * what child components need to understand to manage this operation in the grids.
 *
 * Refetching here means calling core GraphQL queries that each grid depends on to stay
 * up to date after API actions that alter the grid data, such as additions, updates, and deletions.
 *
 * The main reason this component exists separately from the main grid code is because it uses a React hook,
 * which must be called within a functional component. For example, we cannot call a hook where this component
 * is used in the main grid.
 */
export function GridDataRefetcher({
  apolloClient,
  refetchQueries,
  refresh,
  children
}: Props) {
  // This hook is specifically for use by grid actions that do *not* use GraphQL but that must
  // trigger the refetch after their API operations
  const refetchGridData = useCallback(async () => {
    return Promise.all(
      refetchQueries.map(({ query, variables }) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        apolloClient.query({
          query,
          variables,
          fetchPolicy: 'network-only'
        })
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchQueries])

  // The `refreshAfterNonGraphQLOperation` boolean will allow us to skip refetching
  // GraphQL queries manually with the refetchGridData function, in the rare situation
  // where a consumer is still using GraphQL. If that is the case, it probably is
  // better that it pass the `refetchQueries` array directly into the GraphqL query
  // or mutation function.
  // Hopefully this is just overly cautious and will not be used since we now have
  // a solution to use react-query in place of GraphQL in grid actions.
  // Note this defaults to call `refetchGridData` unless the flag is set to false.
  const refreshGridData = (refreshAfterNonGraphQLOperation = true) => {
    refresh()
    if (refreshAfterNonGraphQLOperation) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refetchGridData()
    }
  }

  return children({
    refreshGridData
  })
}
